import '@atlaskit/css-reset';
import { setGlobalTheme } from '@atlaskit/tokens';
import React, { Suspense, useLayoutEffect } from 'react';
import {
  createBrowserRouter,
  isRouteErrorResponse,
  Outlet as ReactRouterOutlet,
  useSearchParams,
  useRouteError,
} from 'react-router-dom';
import ScreenSizeListener from '../components/ScreenSizeListener';
import { useAppSelector } from '../stores/redux.store';
import { CobrandingProvider, getCurrentCobranding } from '../utilities/cobranding/cobranding';
import FlagContainer from './FlagContainer';
import {
  AuthCallbackErrorPage,
  AuthenticatedLoomWorkspaceCreationPage,
  CancelAccountDeletionPage,
  ChangePasswordPage,
  ChooseMethodPage,
  ConfigureAppPage,
  ConfigurePhonePage,
  ContinueAsPage,
  CreateAccountConfirmationPage,
  DeletionRequestConfirmationPage,
  EmailConfirmationPage,
  ExpiredPasswordPage,
  InactiveAccountPage,
  LoginConsentNoticePage,
  LoginPage,
  LogoutPage,
  MfaEnrollmentPage,
  MfaPromotePage,
  MigrateAccountConfirmationPage,
  MultiFactorPage,
  MultiMfaEnrollmentAddSecurityKeyPage,
  MultiMfaEnrollmentAddAuthenticatorPage,
  OAuthDAGTerminalPage,
  PendingDeletionPage,
  RecoveryPage,
  RegisterSecurityKeyPage,
  RemoveAccountPage,
  ResetPasswordPage,
  SaveRecoveryKeyPage,
  SecurityExpiredLinkPage,
  SecuritySuccessPage,
  SelectAccountPage,
  SignupPage,
  SignupOnboardPage,
  SocialLoginRecoveryPage,
  StepUpSsoPage,
  StepUpStartPage,
  VerifyEmailOtpPage,
  InitiateOtpPage,
  VerifyOrReverifyEmailErrorPage,
  VerifyOrReverifyEmailSentPage,
  WelcomePage,
  WelcomeSentPage,
  FeatureGateDevPanel,
} from './LazyPages';
import { getCurrentCloudType } from '../utilities/env';
import MobileAppMetaTags from '../utilities/MobileAppMetaTags';
import { wrapRoutesWithPageStateReduxSyncer } from '../utilities/hooks/usePageStateReduxSyncer';
import { useDynamicConfigs } from '../utilities/feature-flags/dynamic-config';
import { FullScreenSpinnerWrapper, LoadingSpinner } from '../components/LoadingSpinner';
import ServerErrorInterceptor from './ServerErrorInterceptor';
import UnSupportedMobileBrowserInterceptor from './UnSupportedMobileBrowserInterceptor';
import { BrowserMetricsBoundary } from '../browser-metrics';
import ErrorBoundary from '../components/ErrorBoundary';
import { useAnalyticsClient } from '../utilities/analytics/analytics-web-client';
import { reactRouterFutureFlags } from '../helpers/react-router';
import { loginLoader } from '../pages/login/BasePage';
import { signupLoader } from '../pages/signup/BasePage';
import { cancelAccountDeletionLoader } from './CancelAccountDeletionPage';
import { recoverLoader } from './RecoveryPage';
import { socialRecoveryLoader } from './SocialLoginRecoveryPage';
import { logoutLoader } from './LogoutPage';
import { mfaLoader, recoverExpiredLoader } from './MultiFactorPage';
import { changeEmailLoader } from './EmailConfirmationPage';
import { stepUpStartLoader } from './StepUpStartPage';
import { stepUpSsoLoader } from './StepUpSsoPage';
import { changePasswordLoader } from './ChangePasswordPage';
import { continueAsLoader } from './ContinueAsPage';
import { consentLoader } from './LoginConsentNoticePage/LoginConsentNoticePage';
import { selectAccountLoader } from './SelectAccountPage';
import { verifyEmailLoader } from './VerifyOrReverifyEmailErrorPage';
import { verifyEmailSentLoader } from './VerifyOrReverifyEmailSentPage';
import { securityScreenLoader } from '../pages/login/mfa-promote/BasePage';
import { removeAccountLoader } from './RemoveAccountPage';
import { socialConfirmationLoader } from '../pages/login/create-account-confirmation/BasePage';
import { resetExpiredPasswordLoader } from './ExpiredPasswordPage';
import { migrateConfirmationLoader } from './MigrateAccountConfirmationPage';
import { signupVerifyEmailOtpLoader } from '../pages/signup/verify-email/otp/BasePage';
import { signupInitiateOtpLoader } from '../pages/signup/verify-email/initiate-otp/BasePage';
import { welcomeLoader } from '../pages/signup/welcome/BasePage';
import { welcomeSentLoader } from './WelcomeSentPage';
import { isPlaywrightIntegrationEnvironment } from '../utilities/is-playwright-integration-environment';
import { NavigateToLogin } from './MaybeComponentOrRedirectToLogin/NavigateToLogin';
import { MaybeComponentOrRedirectToLogin } from './MaybeComponentOrRedirectToLogin/MaybeComponentOrRedirectToLogin';
import { MaybeAWSWafCaptchaModal } from '../aws-captcha/aws-captcha';
import { AwsCaptchaSuccessCallbackProvider } from '../aws-captcha/aws-captcha-success-callback-context';
import { MaybeLoadAwsCaptchaSdkScript } from '../aws-captcha/maybe-load-aws-captcha-sdk-script';

export function App() {
  useLayoutEffect(() => {
    void setGlobalTheme({
      colorMode: 'light',
      typography: 'typography',
    });
  }, []);

  const analyticsClient = useAnalyticsClient();

  const microbranding = useAppSelector(state => state.microbranding);
  const [searchParams] = useSearchParams();

  const dynamicConfigs = useDynamicConfigs();

  const isGlobalStateLoading = useAppSelector(state => state.globalStateLoading);

  if (isGlobalStateLoading) {
    return (
      <FullScreenSpinnerWrapper>
        <LoadingSpinner testId="global-state-loading-spinner" />
      </FullScreenSpinnerWrapper>
    );
  }

  return (
    <ErrorBoundary analyticsClient={analyticsClient}>
      <AwsCaptchaSuccessCallbackProvider>
        <ServerErrorInterceptor>
          <UnSupportedMobileBrowserInterceptor>
            <BrowserMetricsBoundary>
              <Suspense fallback={<LoadingSpinner testId="suspense-loading-spinner" />}>
                <MobileAppMetaTags />
                <CobrandingProvider
                  value={{
                    cobranding: getCurrentCobranding(searchParams, dynamicConfigs),
                    isMobileApp: microbranding.isMobileApp,
                  }}>
                  <ScreenSizeListener key="screenSizeListener">
                    {/* React Router elements will be rendered in the ReactRouterOutlet */}
                    <ReactRouterOutlet />
                    <FlagContainer />
                    <MaybeLoadAwsCaptchaSdkScript />
                    <MaybeAWSWafCaptchaModal />
                    {/* This a development tool used to be able to see and override feature gate values */}
                    {process.env.NODE_ENV === 'development' &&
                      !isPlaywrightIntegrationEnvironment() && (
                        <Suspense>
                          <FeatureGateDevPanel />
                        </Suspense>
                      )}
                  </ScreenSizeListener>
                </CobrandingProvider>
              </Suspense>
            </BrowserMetricsBoundary>
          </UnSupportedMobileBrowserInterceptor>
        </ServerErrorInterceptor>
      </AwsCaptchaSuccessCallbackProvider>
    </ErrorBoundary>
  );
}

export const createAppRouter = () =>
  createBrowserRouter(
    [
      {
        element: <App />,
        errorElement: <RouterErrorElement />,
        children: wrapRoutesWithPageStateReduxSyncer([
          {
            path: '/2step',
            children: [
              {
                index: true,
                element: <NavigateToLogin />,
              },
              {
                path: 'enrollment',
                children: [
                  { index: true, element: <MfaEnrollmentPage /> },
                  { path: '1-get-app', element: <ChooseMethodPage /> },
                  { path: '2-configure-app', element: <ConfigureAppPage /> },
                  { path: '2-configure-phone', element: <ConfigurePhonePage /> },
                  { path: '2-register-security-key', element: <RegisterSecurityKeyPage /> },
                  { path: '3-connect-phone', element: <ConfigureAppPage isConnectPhone /> },
                  { path: '4-save-recovery-key', element: <SaveRecoveryKeyPage /> },
                  {
                    path: 'add',
                    // These pages are for enrolling multiple methods of MFA
                    // for now these are dummy pages, and is subject to move (path, content, and/or location)
                    children: [
                      {
                        path: 'security-key',
                        element: <MultiMfaEnrollmentAddSecurityKeyPage />,
                      },
                      {
                        path: 'authenticator',
                        element: <MultiMfaEnrollmentAddAuthenticatorPage />,
                      },
                    ],
                  },
                ],
              },
              {
                path: 'recover',
                children: [
                  { index: true, element: <RecoveryPage />, loader: recoverLoader },
                  {
                    path: 'expired',
                    element: <MultiFactorPage isExpired />,
                    loader: recoverExpiredLoader,
                  },
                ],
              },
            ],
          },
          { path: '/error', element: <AuthCallbackErrorPage /> },
          {
            path: '/login',
            children: [
              {
                index: true,
                element: <LoginPage />,
                loader: loginLoader,
              },
              {
                path: 'cancel-account-deletion',
                element: <CancelAccountDeletionPage />,
                loader: cancelAccountDeletionLoader,
              },
              {
                path: 'changepassword',
                element: <ChangePasswordPage />,
                loader: changePasswordLoader,
              },
              {
                path: 'changepassword/p/*', // Used for deeplinking for auth on the mobile apps
                element: <ChangePasswordPage />,
                loader: changePasswordLoader,
              },
              { path: 'consent', element: <LoginConsentNoticePage />, loader: consentLoader },
              { path: 'continue-as', element: <ContinueAsPage />, loader: continueAsLoader },
              {
                path: 'deletion-request-confirmation',
                element: <DeletionRequestConfirmationPage />,
              },
              { path: 'inactive', element: <InactiveAccountPage /> },
              { path: 'mfa', element: <MultiFactorPage isExpired={false} />, loader: mfaLoader },
              { path: 'pending-deletion', element: <PendingDeletionPage /> },
              {
                path: 'remove-account',
                element: <RemoveAccountPage />,
                loader: removeAccountLoader,
              },
              {
                path: 'resetexpiredpassword',
                element: <ExpiredPasswordPage />,
                loader: resetExpiredPasswordLoader,
              },
              { path: 'resetpassword', element: <ResetPasswordPage /> },
              {
                path: 'select-account',
                element: <SelectAccountPage />,
                loader: selectAccountLoader,
              },
              {
                path: 'security-screen',
                element: <MfaPromotePage />,
                loader: securityScreenLoader,
              },
              {
                path: 'social/confirmation',
                element: <CreateAccountConfirmationPage />,
                loader: socialConfirmationLoader,
              },
            ],
          },
          { path: '/logout', element: <LogoutPage />, loader: logoutLoader },
          {
            path: '/mf',
            element: <MultiFactorPage isExpired={false} />,
          },
          {
            path: '/security',
            children: [
              { index: true, element: <NavigateToLogin /> },
              { path: 'expiredlink', element: <SecurityExpiredLinkPage /> },
              {
                path: 'successfulreset',
                element: <SecuritySuccessPage successType="password-reset" />,
              },
              {
                path: 'successfulverification',
                element: <SecuritySuccessPage successType="verification" />,
              },
              {
                path: 'dagterminal',
                element: <OAuthDAGTerminalPage />,
              },
            ],
          },
          {
            path: '/signup',
            children: [
              {
                index: true,
                element: <SignupCommercial />,
                loader: signupLoader,
              },
              { path: 'onboard', element: <SignupOnboardCommercial /> },
              {
                path: 'migrate-confirmation',
                element: <MigrateAccountConfirmationPage />,
                loader: migrateConfirmationLoader,
              },
              { path: 'welcome', element: <WelcomePage />, loader: welcomeLoader },
              /**
               * The standalone `/welcome/p/loom-create-workspace` page is a step in Loom's signup
               * flow where an existing Atlassian user creates a Loom workspace when creating a new Loom account.
               * In the more common Loom signup flow path where a user is new to Atlassian and Loom,
               * the workspace creation step is shown on the `/welcome` route after a user fills in their name and password.
               * If a user is new to Loom but already exists in Atlassian, this step needs to be hosted
               * on its own page since the login flow requires a redirect.
               *
               * This page will be moved to be hosted on Loom and removed from IDAC once Loom has integrated
               * with Atlassian Identity and can make authenticated requests via Edge Authenticator.
               * See epic: https://useloom.atlassian.net/browse/ID-256
               */
              {
                path: 'welcome/p/loom-create-workspace',
                element: <AuthenticatedLoomWorkspaceCreationPage />,
              },
              { path: 'welcome/p/*', element: <WelcomePage />, loader: welcomeLoader }, // Used for deeplinking for auth on the mobile apps
              { path: 'welcome/sent', element: <WelcomeSentPage />, loader: welcomeSentLoader },
              {
                path: 'verify-email/initiate-otp',
                element: (
                  <MaybeComponentOrRedirectToLogin
                    component={InitiateOtpPage}
                    condition={() => getCurrentCloudType() === 'commercial'}
                  />
                ),
                loader: signupInitiateOtpLoader,
              },
              {
                path: 'verify-email/otp',
                element: (
                  <MaybeComponentOrRedirectToLogin
                    component={VerifyEmailOtpPage}
                    condition={() => getCurrentCloudType() === 'commercial'}
                  />
                ),
                loader: signupVerifyEmailOtpLoader,
              },
            ],
          },
          {
            path: '/social/recovery',
            element: <SocialLoginRecoveryPage />,
            loader: socialRecoveryLoader,
          },
          {
            path: '/step-up',
            children: [
              { path: 'start', element: <StepUpStartPage />, loader: stepUpStartLoader },
              { path: 'sso', element: <StepUpSsoPage />, loader: stepUpSsoLoader },
            ],
          },
          {
            path: '/verify-email/*',
            children: [
              {
                path: 'change-email',
                element: <EmailConfirmationPage />,
                loader: changeEmailLoader,
              },
              {
                path: 'sent',
                element: <VerifyOrReverifyEmailSentPage />,
                loader: verifyEmailSentLoader,
              },
              { path: '*', element: <VerifyOrReverifyEmailErrorPage />, loader: verifyEmailLoader },
            ],
          },
        ]),
      },
    ],
    { future: reactRouterFutureFlags }
  );

function SignupCommercial() {
  if (getCurrentCloudType() === 'commercial') {
    return <SignupPage />;
  }
  return <NavigateToLogin />;
}

function SignupOnboardCommercial() {
  if (getCurrentCloudType() === 'commercial') {
    return <SignupOnboardPage />;
  }
  return <NavigateToLogin />;
}

function RouterErrorElement() {
  const error = useRouteError();
  const analyticsClient = useAnalyticsClient();

  if (isRouteErrorResponse(error) && error.status === 404) {
    analyticsClient.trackingEvent({
      page: 'RouterErrorElement',
      action: 'error',
      subject: 'routeNotFound',
    });
    return <NavigateToLogin />;
  }

  // rethrow to let the parent error boundary handle it
  throw error;
}
